import { REGEX } from '@lessonPlanner/regex';

import { CategorizedComboboxOption, GroupedOptions, OptionItem } from './types';

// Helper function to determine option depth
export function getOptionDepth(options: OptionItem[]): number {
  if (!options || !options.length) return 0;

  // Check if this is a flat array of OptionType objects
  if ('value' in options[0] && 'label' in options[0]) {
    return 1;
  }

  // Check for deeper structures
  if ('items' in options[0] && Array.isArray(options[0].items)) {
    return 1 + getOptionDepth(options[0].items);
  }

  return 0;
}

export function flattenOptions(
  options: OptionItem[],
): CategorizedComboboxOption[] {
  const result: CategorizedComboboxOption[] = [];

  // Check if this is a simple 2-level structure (group -> items)
  const isSimpleTwoLevel =
    options.length > 0 &&
    options.every(
      (item) =>
        !item.value &&
        item.items &&
        item.items.every((subItem) => subItem.value !== undefined),
    );

  // For simple two-level structures, explicitly use "values" domain type for color assignments
  const domainType = isSimpleTwoLevel ? 'values' : undefined;

  // Process options recursively, building path as we go
  function processLevel(
    items: OptionItem[],
    path: { key: string; value: string }[] = [],
  ) {
    items.forEach((item) => {
      if (item.value !== undefined && item.label) {
        // This is a leaf node, create a flattened option
        const option: CategorizedComboboxOption = {
          id: item.value,
          value: item.value,
          name: item.label,
        };

        // Add path information to the option
        path.forEach((pathItem) => {
          option[pathItem.key] = pathItem.value;
        });

        // For simple two-level structures, ensure we set domain to the values type
        // This helps with color scheme application
        if (
          isSimpleTwoLevel &&
          domainType &&
          path.length > 0 &&
          !option.domain
        ) {
          option.domainType = domainType;
        }

        result.push(option);
      } else if (item.items?.length) {
        // This is a branch node, recurse to next level
        const newPath = [...path];
        if (item.label) {
          // Determine what key to use based on the depth
          let key = 'layer';

          // Special handling for simple 2-level structures like values list
          if (isSimpleTwoLevel) {
            key = 'domain'; // Use domain for cleaner grouping in the UI
          } else {
            // Standard depth-based key assignment
            if (path.length === 0) key = 'layer';
            else if (path.length === 1) key = 'domain';
            else key = 'category';
          }

          newPath.push({ key, value: item.label });
        }
        processLevel(item.items, newPath);
      }
    });
  }

  processLevel(options);
  return result;
}

/**
 * Filters options based on search query
 */
export function filterOptions(
  options: CategorizedComboboxOption[],
  query: string,
): CategorizedComboboxOption[] {
  if (!query) {
    return options;
  }

  const normalizedQuery = query
    .toLowerCase()
    .replace(REGEX.REMOVE_WHITESPACE, '');

  return options.filter((option) => {
    if (!option?.name) return false;

    const normalizedName = option.name
      .toLowerCase()
      .replace(REGEX.REMOVE_WHITESPACE, '');

    return normalizedName.includes(normalizedQuery);
  });
}

/**
 * Groups options based on their hierarchical structure
 */
export function groupOptions(
  options: CategorizedComboboxOption[],
): GroupedOptions {
  const grouped: GroupedOptions = {};

  // Determine the maximum depth of the data
  const hasLayers = options.some((option) => option.layer);
  const hasDomains = options.some((option) => option.domain);
  const hasCategories = options.some((option) => option.category);

  // Detect if this is a simple 2-level structure (group -> items)
  const isSimpleTwoLevel =
    !hasLayers &&
    hasDomains &&
    !hasCategories &&
    options.length > 0 &&
    options.every((option) => option.domain !== 'default');

  options.forEach((option) => {
    if (!option?.value) return;

    let layerKey = 'default';
    let domainKey = 'default';
    let categoryKey = 'default';

    // For flat options (no hierarchy), use a single layer
    if (!hasLayers && !hasDomains && !hasCategories) {
      layerKey = 'Items';
    }
    // For options with only domain level (simple grouping)
    else if (isSimpleTwoLevel) {
      // For two-level structures, use 'Values' as the main layer
      // This ensures color styling works properly
      layerKey = 'Values';
      domainKey = option.domain || 'default';
    }
    // For options with layer and domain (two true levels)
    else if (hasLayers && hasDomains && !hasCategories) {
      layerKey = option.layer || 'default';
      domainKey = option.domain || 'default';
    }
    // For options with full 3 levels
    else if (hasLayers && hasDomains && hasCategories) {
      layerKey = option.layer || 'default';
      domainKey = option.domain || 'default';
      categoryKey = option.category || 'default';
    }

    // Initialize layered structure as needed
    if (!grouped[layerKey]) {
      grouped[layerKey] = {};
    }

    if (!grouped[layerKey][domainKey]) {
      grouped[layerKey][domainKey] = {};
    }

    if (!grouped[layerKey][domainKey][categoryKey]) {
      grouped[layerKey][domainKey][categoryKey] = [];
    }

    grouped[layerKey][domainKey][categoryKey].push(option);
  });

  return grouped;
}

export function areOptionsEqual(
  option1: CategorizedComboboxOption | undefined,
  option2: CategorizedComboboxOption | undefined,
): boolean {
  if (!option1?.value || !option2?.value) return false;

  return (
    option1.value === option2.value &&
    option1.domain === option2.domain &&
    option1.category === option2.category &&
    option1.layer === option2.layer
  );
}

export function isOptionSelected(
  option: CategorizedComboboxOption,
  selectedValues: CategorizedComboboxOption[],
): boolean {
  if (!option?.value) return false;
  return selectedValues.some((item) => areOptionsEqual(item, option));
}

export function handleSelectionChange(
  selected: CategorizedComboboxOption | CategorizedComboboxOption[],
  multiSelect: boolean,
  selectedValues: CategorizedComboboxOption[],
  maxSelections: number,
  onChange: (
    value: CategorizedComboboxOption | CategorizedComboboxOption[],
  ) => void,
  resetQuery?: () => void,
  maximumDomainItems?: number,
) {
  const canAddMore = selectedValues.length < maxSelections;

  if (multiSelect) {
    // If we're receiving a single item and we're in multi-select mode
    if (!Array.isArray(selected)) {
      if (!selected?.value) return;

      const isAlreadySelected = selectedValues.some((item) =>
        areOptionsEqual(item, selected),
      );

      if (isAlreadySelected) {
        onChange(
          selectedValues.filter((item) => !areOptionsEqual(item, selected)),
        );
      } else if (
        canAddMore &&
        canAddToDomain(selected, selectedValues, maximumDomainItems)
      ) {
        onChange([...selectedValues, selected]);
      }
    } else {
      onChange(selected);
    }
  } else {
    // Single-select mode always replaces the current value
    if (Array.isArray(selected)) {
      onChange(selected[0]);
    } else {
      onChange(selected);
    }
  }

  if (resetQuery) {
    resetQuery();
  }
}

export function normalizeValue(
  value: CategorizedComboboxOption | CategorizedComboboxOption[] | undefined,
  multiSelect: boolean,
): CategorizedComboboxOption[] {
  if (!value) {
    return [];
  }

  if (Array.isArray(value)) {
    return multiSelect ? value : value.slice(0, 1);
  }

  return [value];
}

export function countSelectedItemsByDomain(
  selectedValues: CategorizedComboboxOption[],
): Record<string, number> {
  const counts: Record<string, number> = {};

  selectedValues.forEach((item) => {
    if (item.domain) {
      counts[item.domain] = (counts[item.domain] || 0) + 1;
    }
  });

  return counts;
}

export function canAddToDomain(
  option: CategorizedComboboxOption,
  selectedValues: CategorizedComboboxOption[],
  maximumDomainItems?: number,
): boolean {
  if (!maximumDomainItems || !option.domain) {
    return true;
  }

  const domainCounts = countSelectedItemsByDomain(selectedValues);
  return (domainCounts[option.domain] || 0) < maximumDomainItems;
}
